import {
  Activity,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import styled, { useTheme } from "styled-components";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { LabelStackView } from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import {
  Button,
  Container,
  Label,
  Loader,
  Message,
  Popup,
} from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import IdeaBody from "components/ideas/Body";
import { LaneInfo } from "components/ideas/Header";
import { IdeaProvider } from "components/ideas/IdeaProvider";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { useIdea } from "components/ideas/hooks/useIdea";
import {
  AnonymousChip,
  IdeaBusinessProfileChip,
  UserChip,
} from "components/lib/Chips";
import { TagView } from "components/lib/Choosers/Tags/TagModalChooser";
import { HoverAction, HoverActions } from "components/lib/HoverActions";
import SlidingModal from "components/lib/SlidingModal";
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI";

import PreviewSideWindow from "./PreviewSideWindow/PreviewSideWindow";
import type {
  HandledIdeaPreviewProps,
  IdeaPreviewChallenge,
  IdeaPreviewIdea,
  IdeaPreviewProps,
} from "./types";

export type { HandledIdeaPreviewProps, IdeaPreviewProps } from "./types";

const noopUpdateProjectLane = (
  _ideaIds: string[],
  _laneId: string,
  _order?: number,
) => {};

const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const PreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f2f2f3;
`;

const PreviewHeader = styled.div<{ $coverImage?: string }>`
  width: 100%;
  position: relative;

  .overlay {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.21);
    z-index: 11;
  }
  .overlay-body {
    position: relative;
    z-index: 12;
    display: flex;
    flex-direction: column;
    padding: 10px;
  }
  top: 0;
  left: 0;
  ${({ $coverImage }) =>
    $coverImage ?
      `
    background-image: url(${$coverImage});
    background-size:cover;
    background-position: center center;
  `
    : `
    background-color: #e9ebee;
  `}
  border-bottom: 1px solid lightgray;
  z-index: 999;
  min-height: 175px;
  overflow: hidden;
  text-overflow: ellipsis;
  .ui.button {
    border: 0.5px solid gray;
  }
  h1 {
    font-size: 2.3em;
    margin-top: 20px;
    margin-bottom: 10px;
    color: white;
    line-height: 1.15em;
  }
`;

const IdeaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 13;
  background-color: #f2f2f3;
`;

const BodyContainer = styled.div`
  width: 100%;
  padding: 10px;
`;

const FlexRow = styled.div<{ $wrap?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({ $wrap }) =>
    $wrap &&
    `
    flex-wrap: wrap;
    margin-top: -7.5px;
    > * {
      margin-top: 7.5px;
    }
  `}
`;

type IdeaPreviewComponentProps = IdeaPreviewProps;

const IdeaPreview = ({
  isOpen,

  challenge: propChallenge,
  onCloseModal = () => {},
  onAfterCloseModal,

  moveIdea,
  canGoNextIdea,
  canGoPrevIdea,
  assessmentEnabled,
  updateProjectLane = noopUpdateProjectLane,
  removeIdeaFromMerge,
  removingIdeaFromMerge,
  currentIdeaPosition,
  totalIdeas,
  redirectToBoard,
  page = 1,
  limit = 0,
}: IdeaPreviewComponentProps) => {
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [modalIdeaWidth, setModalIdeaWidth] = useState(0);
  const [commentRef, setCommentRef] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setSectionRef = (ref: HTMLDivElement | null, itemId: string) => {
    sectionRefs.current[itemId] = ref;

    if (itemId === "comments") {
      setCommentRef(ref);
    }
  };
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setFieldRef = (ref: HTMLDivElement | null, itemId: string) => {
    fieldRefs.current[itemId] = ref;
  };

  const { t } = useTranslation();

  const theme = useTheme();
  const challengeId = idea?.challenge;
  const board = useMemo(
    () => idea?.projectManagement?.boards?.find((b) => b.forId === challengeId),
    [idea, challengeId],
  );
  const ideaId = idea?._id;

  const { clearAssessmentRound } = useAssessmentEditor(idea);

  // only show pm board management actions if the user can manage challenge (collaborator/owner/admins) or is assigned to the idea
  const canManageChallenge = util.canManageChallenge(
    user!,
    propChallenge ?? idea?.ownerChallenge,
  );
  const canViewProjectBoard =
    canManageChallenge ||
    ((board?.assignees || []).indexOf(user!._id) > -1 &&
      idea?.ownerChallenge?.projectManagementVisibility === "users") ||
    util.hasPermission(user!, "challenge.viewProjectBoard", challengeId ?? "");

  const handleCloseModal = useCallback(() => {
    setModalIdeaWidth(0);
    clearAssessmentRound();
    onCloseModal();
  }, [clearAssessmentRound, onCloseModal]);

  const onOpenPreview = useCallback(() => {
    const rect = previewContainerRef.current?.getBoundingClientRect();
    setModalIdeaWidth(rect?.width ?? 0);
  }, []);

  useEffect(() => {
    if (idea) {
      api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
        ...prev,
        ideas: (prev.ideas || []).map((oldIdea) => {
          if (oldIdea._id !== idea._id) return oldIdea;
          return { ...oldIdea, currentUserHasViewed: true };
        }),
      }));
    }
  }, [idea]);

  useEffect(() => {
    if (commentRef && location.hash === "#comments") {
      commentRef.scrollIntoView({ behavior: "smooth", block: "start" });
      navigate(location.search);
    }
  }, [commentRef, location.hash, location.search, navigate]);

  return (
    <SlidingModal
      isOpen={isOpen}
      onOpen={onOpenPreview}
      onClose={handleCloseModal}
      onAfterClose={onAfterCloseModal}
      width={theme?.sizes.isComputer ? "60%" : "100%"}
      ref={previewContainerRef}
    >
      <ModalContainer>
        {theme.sizes.isComputer && modalIdeaWidth > 0 ?
          <HoverActions $width={modalIdeaWidth}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <HoverAction icon="close" onClick={() => handleCloseModal()}>
                Close
              </HoverAction>
              {moveIdea && isOpen && (canGoPrevIdea || canGoNextIdea) ?
                <div style={{ display: "flex", gap: 8 }}>
                  <Popup
                    on="hover"
                    trigger={
                      <div>
                        <HoverAction
                          icon="arrow left"
                          onClick={() => moveIdea("prev")}
                          margined={!(currentIdeaPosition && totalIdeas)}
                          disabled={!canGoPrevIdea}
                        />
                      </div>
                    }
                    content={`Go to previous ${t("generic.idea")}`}
                  />

                  {currentIdeaPosition &&
                    totalIdeas &&
                    (canGoNextIdea || canGoPrevIdea) && (
                      <HoverAction disabled>
                        {t("common:capitalise", { key: "generic.idea" })}{" "}
                        {(page - 1) * limit + currentIdeaPosition} of{" "}
                        {totalIdeas}
                      </HoverAction>
                    )}
                  <Popup
                    trigger={
                      <div>
                        <HoverAction
                          icon="arrow right"
                          onClick={() => moveIdea("next")}
                          disabled={!canGoNextIdea}
                        />
                      </div>
                    }
                    content={`Go to next ${t("generic.idea")}`}
                  />
                </div>
              : null}
            </div>

            <div className="hover-actions-inner">
              <PreviewSideWindow
                assessmentEnabled={assessmentEnabled}
                updateProjectLane={updateProjectLane}
                challenge={propChallenge as IdeaPreviewChallenge | undefined}
                sectionRefs={sectionRefs}
                fieldRefs={fieldRefs}
                scrollRef={previewContainerRef}
              />
            </div>
          </HoverActions>
        : null}
        <PreviewContainer>
          {!loading && !idea ?
            <Container style={{ height: "100vh" }}>
              <Message error style={{ margin: "2rem 0" }}>
                <Message.Header>
                  Unable to load {t("generic.idea")}
                </Message.Header>
                <p>
                  Sorry, we were unable to load this {t("generic.idea")}. Please
                  try again later.
                </p>
              </Message>
              <Button
                content={`Close`}
                icon="chevron left"
                onClick={() => handleCloseModal()}
              />
            </Container>
          : <>
              <PreviewHeader
                $coverImage={util.ideaCoverImage(idea, "2560x400")}
              >
                <div className="overlay" />
                <div className="overlay-body">
                  <FlexRow>
                    <ModalContainer style={{ justifyContent: "flex-end" }}>
                      {!loading ?
                        <>
                          <Button
                            primary
                            size="tiny"
                            content={`Open full ${t("generic.idea")}`}
                            icon="file alternate"
                            as={Link}
                            to={`/ideas/${ideaId}`}
                          />
                          {redirectToBoard && (
                            <Button
                              primary
                              size="tiny"
                              content={`View in project board`}
                              icon="file alternate"
                              as={Link}
                              to={`/challenges/${challengeId}/board?idea=${ideaId}`}
                            />
                          )}
                          {!theme.sizes.isComputer ?
                            <Button
                              size="tiny"
                              content={`Close preview`}
                              onClick={() => handleCloseModal()}
                            />
                          : null}
                        </>
                      : null}
                    </ModalContainer>
                  </FlexRow>
                  <div style={{ paddingLeft: 10 }}>
                    <div style={{ marginTop: 10 }}>
                      {loading ?
                        <PlaceholderStack
                          gap={20}
                          style={{ width: 400, marginTop: 60 }}
                        >
                          <PlaceholderItem height={40} style={{ width: 400 }} />
                          <PlaceholderItem height={25} style={{ width: 200 }} />
                          <PlaceholderItem height={30} style={{ width: 200 }} />
                        </PlaceholderStack>
                      : <>
                          <h1>{idea?.name}</h1>
                          <h5 style={{ marginTop: 0, color: "white" }}>
                            <span style={{ textTransform: "uppercase" }}>
                              {t("generic.idea")}
                            </span>
                            {idea.ownerChallenge && (
                              <span style={{ color: "white" }}>
                                {" "}
                                For{" "}
                                <Link
                                  style={{ color: "white" }}
                                  to={`/challenges/${idea.challenge}`}
                                >
                                  {idea.ownerChallenge?.name}
                                </Link>
                              </span>
                            )}
                          </h5>
                          <div style={{ display: "flex", marginBottom: 10 }}>
                            {((board as { laneObject?: { laneCount?: number } })
                              ?.laneObject?.laneCount ?? 0) > 0 &&
                              util.canManageChallenge(
                                user!,
                                idea.ownerChallenge,
                              ) && (
                                <>
                                  <LaneInfo
                                    board={board}
                                    idea={idea}
                                    style={{ marginRight: 10 }}
                                  />
                                  <div
                                    style={{
                                      borderLeft: "2px solid white",
                                      height: 24,
                                      marginRight: 10,
                                    }}
                                  ></div>
                                </>
                              )}

                            {!loading ?
                              idea.isSubmitted ?
                                <Popup
                                  trigger={
                                    <Label
                                      style={{ maxHeight: "fit-content" }}
                                      color="green"
                                    >
                                      Submitted
                                    </Label>
                                  }
                                  content={`Submitted ${formatDate(idea.submittedAt)}`}
                                />
                              : <Label
                                  style={{ maxHeight: "fit-content" }}
                                  color="yellow"
                                >
                                  Draft
                                </Label>

                            : null}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <div style={{ color: "#fff" }}>
                              <TagView tags={idea.ownerTags ?? []} />
                              <LabelStackView
                                labels={idea.labels ?? []}
                                horizontal
                              />
                              <span style={{ color: "white", marginRight: 5 }}>
                                Created by{" "}
                              </span>
                              {!idea.authors || idea.authors.length === 0 ?
                                <AnonymousChip />
                              : null}
                              {idea.lockedOwnerIdeaBusinessProfile ?
                                <IdeaBusinessProfileChip
                                  ideaBusinessProfile={
                                    idea.lockedOwnerIdeaBusinessProfile
                                  }
                                  inverted
                                  style={{ marginRight: 15 }}
                                />
                              : null}
                              {idea.authors ?
                                idea.authors.map((a) => (
                                  <UserChip
                                    user={a}
                                    key={a._id}
                                    inverted
                                    style={{
                                      marginRight: 15,
                                      marginBottom: 4,
                                      marginTop: 10,
                                    }}
                                  />
                                ))
                              : null}
                            </div>
                          </div>
                        </>
                      }
                    </div>
                  </div>
                </div>
              </PreviewHeader>
              <IdeaContainer>
                <BodyContainer>
                  {(
                    !theme.sizes.isComputer &&
                    idea &&
                    !loading &&
                    canViewProjectBoard
                  ) ?
                    <PreviewSideWindow
                      assessmentEnabled={assessmentEnabled}
                      updateProjectLane={updateProjectLane}
                      challenge={
                        propChallenge as IdeaPreviewChallenge | undefined
                      }
                      fieldRefs={fieldRefs}
                      sectionRefs={sectionRefs}
                      scrollRef={previewContainerRef}
                    />
                  : null}
                  <IdeaBody
                    key={idea?._id ?? ""}
                    idea={idea}
                    setSectionRef={setSectionRef}
                    sectionRefs={sectionRefs}
                    setFieldRef={setFieldRef}
                    fieldRefs={fieldRefs}
                    loading={loading}
                    isEditing={false}
                    scrollRef={previewContainerRef}
                    removeIdeaFromMerge={removeIdeaFromMerge}
                    removingIdeaFromMerge={removingIdeaFromMerge}
                    updateIdea={(value, path) => {
                      util.onUpdateIdea(idea, value, path);
                    }}
                  />
                </BodyContainer>
              </IdeaContainer>
            </>
          }
        </PreviewContainer>
      </ModalContainer>
    </SlidingModal>
  );
};

type HandledIdeaPreviewComponentProps = HandledIdeaPreviewProps;

export const HandledIdeaPreview = ({
  ideaId: propIdeaId,
  removeIdeaFromMerge: propRemoveIdeaFromMerge,
  updateProjectLane: propUpdateProjectLane,
  ...rest
}: HandledIdeaPreviewComponentProps) => {
  const [ideaId, setIdeaId] = useState<string | null>(propIdeaId);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!propIdeaId) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    setIdeaId(propIdeaId);
  }, [propIdeaId]);

  const ideaQuery = useQuery(
    initUseQuery(api.ideas.get, [ideaId!], {
      isEnabled: !!ideaId,
      select: (data) => ({ _id: data._id, challenge: data.challenge }),
    }),
  );

  const removeIdeaFromMerge = useCallback(
    (mergeIdeaId: string, childIdeaId: string) => {
      propRemoveIdeaFromMerge?.(mergeIdeaId, childIdeaId, () => {
        if (mergeIdeaId === ideaId && ideaId) {
          api.setQueryData(api.ideas.get, [ideaId], (prev) => {
            if (!prev || typeof prev !== "object") return prev;
            const ideaData = prev as IdeaPreviewIdea;
            return {
              ...(ideaData as object),
              children: (ideaData.children ?? []).filter(
                (c: string) => c !== childIdeaId,
              ),
              ownerChildren: (ideaData.ownerChildren ?? []).filter(
                (c) => c._id !== childIdeaId,
              ),
            } as IdeaPreviewIdea;
          });
        }
      });
    },
    [ideaId, propRemoveIdeaFromMerge],
  );

  const updateProjectLane = useCallback(
    (movingIdeaIds: string[], laneId: string, order: number = 0) => {
      const challengeId = ideaQuery.data?.challenge;
      if (!challengeId || !movingIdeaIds.length || !laneId) {
        return;
      }

      api.boards.moveProjectIdeas(
        challengeId,
        {
          ideaIds: movingIdeaIds,
          targetLane: laneId,
          order,
          sendAnnouncements: [],
        },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => {
            if (!prev?.ideas || !updatedIdeas) return prev;

            return {
              ...prev,
              ideas: prev.ideas.map((idea) => {
                const matchingUpdatedIdea = updatedIdeas.find(
                  (updated) => updated._id === idea._id,
                );
                if (!matchingUpdatedIdea) return idea;
                return {
                  ...idea,
                  projectManagement: matchingUpdatedIdea.projectManagement,
                };
              }),
            };
          });
        },
        () => {},
      );
    },
    [ideaQuery.data?.challenge],
  );

  return (
    <IdeaProvider ideaId={ideaId ?? undefined}>
      <IdeaPreview
        {...rest}
        isOpen={isOpen}
        removeIdeaFromMerge={removeIdeaFromMerge}
        updateProjectLane={propUpdateProjectLane ?? updateProjectLane}
        onAfterCloseModal={() => setIdeaId(null)}
      />
    </IdeaProvider>
  );
};

export default IdeaPreview;
